import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getLionAutoTemplates,
  getLionAutoTemplateShows,
  saveLionAutoTemplate,
  updateLionAutoTemplate,
  deleteLionAutoTemplate,
  applyLionAutoTemplate,
} from '../api';
import { lionAutoKeys } from './useLionAuto';

// Lion Auto templates (LaTemplatesModal): a show's groups saved once and
// applied to the artist's other dates. Ids are held as strings so a numeric
// id from an API body never misses the cache entry.
export const lionAutoTemplateKeys = {
  // Every template query (prefix for invalidation)
  all: ['lionAuto', 'templates'],
  // The templates of the show's performer, as seen from that show (linkedHere)
  list: (eventId) => ['lionAuto', 'templates', 'list', String(eventId)],
  // The shows one template can be applied to; no id = every template's
  shows: (id) => (id === undefined ? ['lionAuto', 'templates', 'shows'] : ['lionAuto', 'templates', 'shows', String(id)]),
};

// The show's performer's templates. DB only on the server and shared by all
// staff; each opening of the modal reads afresh (staleTime 0), so Apply sends
// the version just read. The default retry (none on 401 / 403 / 404 / 429).
// Only read while the modal is open.
export function useLionAutoTemplates(eventId, { enabled = true } = {}) {
  return useQuery({
    queryKey: lionAutoTemplateKeys.list(eventId),
    queryFn: async () => (await getLionAutoTemplates(eventId)).data,
    enabled: !!eventId && enabled,
    staleTime: 0,
  });
}

// The shows a template can be applied to, with their linked counts and
// version. Each opening of the Apply list reads afresh (linked counts move
// with every apply, here or in another tab).
export function useLionAutoTemplateShows(id, enabled = true) {
  return useQuery({
    queryKey: lionAutoTemplateKeys.shows(id),
    queryFn: async () => (await getLionAutoTemplateShows(id)).data,
    enabled: id != null && enabled,
    staleTime: 0,
  });
}

// Save, update and delete touch our DB only and are never retried. Each
// changes which of this show's groups are linked (their Template chip), so
// the templates and this show's groups re-read (allGroups: every event's, as
// a delete unlinks the template's groups on all its shows). A 404 or 409
// (someone changed or deleted it first), a 500 or no answer at all (the
// outcome is unknown) re-read the same way.
function useTemplateWrite(eventId, mutationFn, { allGroups = false } = {}) {
  const queryClient = useQueryClient();
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: lionAutoTemplateKeys.all });
    queryClient.invalidateQueries({ queryKey: lionAutoKeys.groups(allGroups ? undefined : eventId) });
  };
  return useMutation({
    mutationFn,
    retry: false,
    onSuccess: refresh,
    onError: (err) => {
      const status = err?.response?.status;
      if (!status || status === 404 || status === 409 || status >= 500) refresh();
    },
  });
}

// variables { name, groupIds } → 201 { template, linked }
export function useSaveLionAutoTemplate(eventId) {
  return useTemplateWrite(eventId, async ({ name, groupIds }) =>
    (await saveLionAutoTemplate({ eventId, name, groupIds })).data);
}

// variables { id, groupIds, name? } → { template, linked, unlinked }
export function useUpdateLionAutoTemplate(eventId) {
  return useTemplateWrite(eventId, async ({ id, groupIds, name }) =>
    (await updateLionAutoTemplate(id, { eventId, groupIds, name })).data);
}

// variables: the template id → { id, unlinked }. Its linked groups on every
// show lose their Template chip: every event's groups re-read.
export function useDeleteLionAutoTemplate(eventId) {
  return useTemplateWrite(eventId, async (id) => (await deleteLionAutoTemplate(id)).data, { allGroups: true });
}

// One show's apply: variables { id, eventId, version } → the apply answer.
// The Apply list calls it one show at a time (runTemplateApply), never in
// parallel. That show's groups and matches re-read (its groups and their
// listings changed; only an open page refetches, others are marked stale),
// as do its templates list (linkedHere) and this template's shows (linked
// counts, version). A 5xx or no answer re-reads the same way (the outcome is
// unknown). A 404 (the template deleted) re-reads every template query: the
// list drops it. A 409 TEMPLATE_STALE re-reads nothing on its own: the list
// says to reload, and the pricer does. Never retried.
export function useApplyLionAutoTemplate() {
  const queryClient = useQueryClient();
  const refreshShow = ({ id, eventId }) => {
    queryClient.invalidateQueries({ queryKey: lionAutoKeys.groups(eventId) });
    queryClient.invalidateQueries({ queryKey: lionAutoKeys.matches(eventId) });
    queryClient.invalidateQueries({ queryKey: lionAutoTemplateKeys.list(eventId) });
    queryClient.invalidateQueries({ queryKey: lionAutoTemplateKeys.shows(id) });
  };
  return useMutation({
    mutationFn: async ({ id, eventId, version }) => (await applyLionAutoTemplate(id, { eventId, version })).data,
    retry: false,
    onSuccess: (_data, variables) => refreshShow(variables),
    onError: (err, variables) => {
      const status = err?.response?.status;
      if (!status || status >= 500) refreshShow(variables);
      if (status === 404) queryClient.invalidateQueries({ queryKey: lionAutoTemplateKeys.all });
    },
  });
}
